import React, { useEffect, useMemo, useCallback } from 'react';
import { Box, Spinner, Alert } from "@cloudscape-design/components";
import { useSplitPanel } from "../../hooks/SplitPanelContext";
import InfoTab from "./tabs/InfoTab";
import AgenteTab from "./tabs/AgenteTab";
import ConclusionTab from "./tabs/ConclusionTab";

export default function InsightDetailPanel({
  selectedCall,
  callDetails,
  loadingDetails,
  insightError,
  activeTabId,
  setActiveTabId,
  history,
  t
}) {
  const { setSplitPanelHeaderContent, closeSplitPanel } = useSplitPanel();

  // Manejar cambio de tab
  const handleTabClick = useCallback((tabId) => {
    setActiveTabId(tabId);
  }, [setActiveTabId]);

  // Crear el header con tabs personalizados
  const tabsHeader = useMemo(() => {
    const tabs = [
      { id: "info", label: t("insights.tabs.info", { defaultValue: "Info" }) },
      { id: "agente", label: t("insights.tabs.agente", { defaultValue: "Agente" }) },
      { id: "conclusion", label: t("insights.tabs.conclusion", { defaultValue: "Conclusión" }) }
    ];

    return (
      <Box
        padding={{ horizontal: "m" }}
        borderBottom="m"
        style={{ 
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          minHeight: '48px'
        }}
      >
        <Box 
          display="flex" 
          alignItems="center"
          justifyContent="center"
          style={{ flexDirection: 'row', width: '100%', flex: 1 }}
        >
          {tabs.map((tab, index) => (
            <React.Fragment key={tab.id}>
              {index > 0 && (
                <Box
                  margin={{ horizontal: "xs" }}
                  variant="awsui-key-label"
                  display="inline"
                >
                  |
                </Box>
              )}
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  onClick={() => handleTabClick(tab.id)}
                  role="tab"
                  aria-selected={activeTabId === tab.id}
                  type="button"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontWeight: activeTabId === tab.id ? 600 : 400,
                    fontFamily: 'inherit',
                    fontSize: '16px',
                    color: 'inherit',
                    margin: 0,
                    outline: 'none',
                    position: 'relative',
                    lineHeight: '1.5',
                    display: 'inline-block'
                  }}
                >
                  {tab.label}
                </button>
                {activeTabId === tab.id && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '2px',
                      backgroundColor: 'var(--color-text-interactive-default, #0972d3)'
                    }}
                  />
                )}
              </div>
            </React.Fragment>
          ))}
        </Box>
      </Box>
    );
  }, [activeTabId, handleTabClick, t]);

  // Actualizar el header cuando cambia el tab activo o cuando se carga el componente
  useEffect(() => {
    if (selectedCall && !loadingDetails && !insightError) {
      setSplitPanelHeaderContent(tabsHeader);
    }
  }, [activeTabId, selectedCall, loadingDetails, insightError, tabsHeader, setSplitPanelHeaderContent]);

  // Si no hay llamada seleccionada
  if (!selectedCall) {
    return (
      <Box textAlign="center" padding="xl">
        {t("insights.noCallSelected", { defaultValue: "No hay llamada seleccionada" })}
      </Box>
    );
  }

  // Mostrar loading state
  if (loadingDetails) {
    return (
      <Box textAlign="center" padding="xl">
        <Spinner />
        <Box margin={{ top: "m" }}>
          {t("insights.loadingInsight", { defaultValue: "Cargando información del insight..." })}
        </Box>
      </Box>
    );
  }

  // Mostrar error state
  if (insightError) {
    return (
      <Box padding="m">
        <Alert type="error" header={t("insights.errorLoadingInsight", { defaultValue: "Error al cargar la información del insight" })}>
          {insightError}
        </Alert>
      </Box>
    );
  }

  // Renderizar el contenido del tab activo
  const renderTabContent = () => {
    switch (activeTabId) {
      case "info":
        return (
          <InfoTab
            selectedCall={selectedCall}
            callDetails={callDetails}
            history={history}
            t={t}
            onClosePanel={closeSplitPanel}
          />
        );
      case "agente":
        return (
          <AgenteTab
            selectedCall={selectedCall}
            callDetails={callDetails}
            t={t}
          />
        );
      case "conclusion":
        return (
          <ConclusionTab
            selectedCall={selectedCall}
            callDetails={callDetails}
            t={t}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div style={{
      backgroundColor: 'var(--color-background-container-content, #ffffff)',
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {renderTabContent()}
    </div>
  );
}
